import { Module } from 'vuex'
import type { ApSubject, ApExamQuestion } from '@/api/apexam'
import { saveApExamSessionSnapshot } from '@/utils/apexamSession'

export interface ApExamSession {
    examId: string
    sourceType: 'MOCK_EXAM' | 'UNIT_PRACTICE' | 'REVIEW_MISSED'
    paperId?: number
    paperName?: string
    subjectId?: number
    subjectName?: string
    unitIds?: string
    difficultyLevel?: 'EASY' | 'MEDIUM' | 'HARD'
    questionCount?: number
    sectionCode?: string
    totalDuration: number
    totalQuestionCount: number
    structure?: any
    questions: ApExamQuestion[]
    startTime: number
    elapsedSeconds?: number
    currentQuestionIndex?: number
    noRecord?: boolean
    guestMode?: boolean
}

export interface ApExamResult {
    examId: string
    sourceType: string
    exameStatus: string
    paperId?: number
    paperName?: string
    subjectId?: number
    subjectName?: string
    sectionCode?: string
    unitIds?: string
    difficultyLevel?: 'EASY' | 'MEDIUM' | 'HARD'
    questionCount?: number
    totalScore?: number
    userTotalScore?: number
    mcqCorrectCount?: number
    mcqTotalCount?: number
    correctRate?: number
    apScore?: number
    practiceScore?: number
    apScoreDescription?: string
    compositeScore?: number
    percentile?: number
    actualCostTime?: number
    totalDuration?: number
    frqConfidence?: number
    weakUnits?: { unitId: number; unitName: string; unitDescription?: string }[]
    estimatedExamTime?: number
    questions?: ApExamQuestion[]
    session?: Partial<ApExamSession> | null
}

export interface ApExamState {
    bootstrapData: ApSubject[] | null
    currentSession: ApExamSession | null
    lastResult: ApExamResult | null
}

const state: ApExamState = {
    bootstrapData: null,
    currentSession: null,
    lastResult: null
}

const getters = {
    bootstrapData: (state: ApExamState) => state.bootstrapData,
    currentSession: (state: ApExamState) => state.currentSession,
    lastResult: (state: ApExamState) => state.lastResult,
    subjectList: (state: ApExamState) => state.bootstrapData || [],
    getSubjectById: (state: ApExamState) => (id: number) =>
        state.bootstrapData?.find(s => s.id === id) || null
}

const mutations = {
    SET_BOOTSTRAP(state: ApExamState, data: ApSubject[]) {
        state.bootstrapData = data
    },
    SET_CURRENT_SESSION(state: ApExamState, session: ApExamSession | null) {
        state.currentSession = session
        saveApExamSessionSnapshot(session)
    },
    SET_LAST_RESULT(state: ApExamState, result: ApExamResult | null) {
        state.lastResult = result
    },
    UPDATE_QUESTION_ANSWER(
        state: ApExamState,
        payload: { examQuestionId: string; userAnswer?: string; userResult?: string; answerRevealed?: boolean }
    ) {
        if (!state.currentSession) return
        state.currentSession.questions = state.currentSession.questions.map(q => {
            if (q.examQuestionId === payload.examQuestionId) {
                return {
                    ...q,
                    _userSelectedAnswer: payload.userAnswer ?? q._userSelectedAnswer,
                    _userInputResult: payload.userResult ?? q._userInputResult,
                    _answerRevealed: payload.answerRevealed ?? q._answerRevealed
                }
            }
            return q
        })
        saveApExamSessionSnapshot(state.currentSession)
    }
}

const actions = {
    setBootstrap({ commit }: any, data: ApSubject[]) {
        commit('SET_BOOTSTRAP', data)
    },
    setCurrentSession({ commit }: any, session: ApExamSession | null) {
        commit('SET_CURRENT_SESSION', session)
    },
    setLastResult({ commit }: any, result: ApExamResult | null) {
        commit('SET_LAST_RESULT', result)
    },
    updateQuestionAnswer({ commit }: any, payload: { examQuestionId: string; userAnswer?: string; userResult?: string; answerRevealed?: boolean }) {
        commit('UPDATE_QUESTION_ANSWER', payload)
    }
}

const apexam: Module<ApExamState, any> = {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
}

export default apexam
